export type Attachment = {
  url: string;
  name: string;
};

export type AttachmentDto = {
  fileName?: null | string;
  fileUrl?: null | string;
};

type MaybeAttachment = Attachment | null | undefined;

const MAX_OTHER_ATTACHMENTS = 4;

export function normalizeOtherAttachments(
  otherFiles: Attachment[] | AttachmentDto[] | undefined
): Attachment[] {
  return (otherFiles ?? [])
    .slice(0, MAX_OTHER_ATTACHMENTS)
    .map((file) => {
      if ('name' in file && 'url' in file) {
        return file.name && file.url ? file : null;
      }

      if (!file.fileName || !file.fileUrl) return null;
      return { name: file.fileName, url: file.fileUrl };
    })
    .filter(
      (attachment): attachment is Attachment =>
        attachment !== null && attachment !== undefined
    );
}

export function getAttachmentAt(
  attachments: MaybeAttachment[],
  index: number
): Attachment | null {
  return attachments[index] ?? null;
}

export function getLastAttachmentIndex(attachments: MaybeAttachment[]): number {
  return attachments.reduce(
    (lastIndex, attachment, index) =>
      attachment !== null && attachment !== undefined ? index : lastIndex,
    -1
  );
}

export function shouldShowAttachmentSlot(
  attachments: MaybeAttachment[],
  index: number
): boolean {
  return index <= getLastAttachmentIndex(attachments) + 1;
}

export function compactAttachments(
  attachments: MaybeAttachment[]
): Attachment[] {
  return attachments.filter(
    (attachment): attachment is Attachment =>
      attachment !== null && attachment !== undefined
  );
}

export function toOtherAttachmentSlotPatch(attachments: MaybeAttachment[]): {
  otherAttachment1: Attachment | null;
  otherAttachment2: Attachment | null;
  otherAttachment3: Attachment | null;
  otherAttachment4: Attachment | null;
} {
  const normalizedAttachments = compactAttachments(attachments);

  return {
    otherAttachment1: normalizedAttachments[0] ?? null,
    otherAttachment2: normalizedAttachments[1] ?? null,
    otherAttachment3: normalizedAttachments[2] ?? null,
    otherAttachment4: normalizedAttachments[3] ?? null,
  };
}

export function toOtherFilesRequest(attachments: MaybeAttachment[]): Array<{
  fileName: string;
  fileUrl: string;
}> {
  return compactAttachments(attachments).map((attachment) => ({
    fileName: attachment.name,
    fileUrl: attachment.url,
  }));
}
